import dynamic from 'next/dynamic';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { logError } from '@zg-rentals/log-error';
import { useAuth } from '@zg-rentals/auth-comet';
import {
  AddAttachments,
  AddMessageLayout,
  hasPersonalContactDetails,
  MESSAGE_STATUS,
  PersonalContactDetailsWarningModal,
  TextareaAutoSize,
  TooltipWrapper,
  useMessageDrafts,
} from '@zg-rentals/satellite-components';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import type React from 'react';
import { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type {
  ConversationList_GetConversationsQuery,
  ConversationList_GetConversationsQueryVariables,
  MessageList_GetConversationQuery,
  MessageList_GetConversationQueryVariables,
  MutationZrm_AddMessageToConversationV2Args,
  RenterProfile_GetAttachmentsByConversationIdQuery,
  RenterProfile_GetAttachmentsByConversationIdQueryVariables,
  Zrm_Attachment,
} from '../../types/GraphqlTypes';
import {
  ConversationList_GetConversationsDocument,
  MessageList_GetConversationDocument,
  RenterProfile_GetAttachmentsByConversationIdDocument,
  useAddMessage_AddMessageToConversationV2Mutation,
  useAddMessage_GetCurrentConversationDataQuery,
} from '../../types/GraphqlTypes';
import {
  emitSendDraftMessageEvent,
  emitSendMessageEvent,
  SEND_MESSAGE_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/sendMessage';
import StringConstants from '../../constants/StringConstants';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import {
  inboxPageAddMessageInputTextToAppendVariable,
  inboxPageClearAndAppendMessageInputTextVariable,
  inboxPageIsAddMessageInputFocusedVariable,
} from '../../lib/apollo';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import formValidationUtils from '../../utils/formValidationUtils';
import stringUtils from '../../utils/stringUtils';
import type { ConversationListConversation } from '../ConversationList';
import type { AttachmentsForMessage } from '../MessageAttachments';
import type { AttachmentsUploadedForAddMessage } from '../AttachmentsUploader';
import UploadedAttachmentList from './components/UploadedAttachmentList';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import QuickResponse from './components/QuickResponse';
import MessageListActions from '../MessageListActions';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { logApolloError } from '../../utils/errorUtils';
import { getCurrentSessionURL } from '../../utils/fullStory';
import { appMarkerLogger } from '../../bootstrap';
import { emitStoreMessageToLocalStorageEvent } from '../../constants/clickstreamEvents/conversationItem';
import {
  emitOpenPersonalInfoWarningModalEvent,
  emitSendAnywayInPersonalInfoWarningModalEvent,
} from '../../constants/clickstreamEvents/personalInfoWarningModal';

const AttachmentsUploader = dynamic(() => import('../AttachmentsUploader'), {
  loading: () => null,
  ssr: false,
});

const logger = appMarkerLogger.child({ name: 'AddMessage' });

export type SetAttachmentsUploadedForAddMessage = (uploadedAttachments: AttachmentsUploadedForAddMessage) => void;

type quickResponseResults = MessageList_GetConversationQuery['ZRM_getConversationById']['quickResponses'];

type Props = {
  hasQuickResponses: boolean | null | undefined;
  quickResponses: quickResponseResults;
  fullMessageCountNumber: number;
};

/**
 * This component serves the input field for adding messages to a conversation
 */
const AddMessage = forwardRef<HTMLDivElement, Props>(
  ({ hasQuickResponses, quickResponses, fullMessageCountNumber }: Props, ref) => {
    const apolloClient = useApolloClient();
    const { isImpersonator, isMobileDevice } = useContext(AppContext);
    const [inputText, setInputText] = useState('');
    const { enableQuickResponse, enablePersonalInfoWarningModal } = useFeatureFlagsHelper();
    const { user } = useAuth();

    const {
      clientTimeZone,
      conversationId,
      daysBack,
      folder,
      isByProperty,
      listingAlias,
      readRepliedStatus,
      renterProgressStatus,
    } = useContext(InboxPageContext);

    const { data: addMessageData } = useQuery(GET_ADD_MESSAGE_INPUT_TEXT, { fetchPolicy: 'cache-only' });
    const {
      inboxPageAddMessageInputTextToAppend,
      inboxPageIsConversationSearchBarFocused,
      inboxPageIsAddMessageInputFocused,
      inboxPageClearAndAppendMessageInputText,
    } = addMessageData;
    const { data: conversationData } = useAddMessage_GetCurrentConversationDataQuery({
      variables: {
        conversationId: conversationId || '',
        listingAlias: listingAlias || '',
        clientTimeZone,
      },
      fetchPolicy: 'cache-only',
    });

    const { personalContactWarningData } = useUserWarnings({
      enablePersonalContactWarning: !!enablePersonalInfoWarningModal,
    });

    const [currentTempMessageId, setCurrentTempMessageId] = useState(String(Date.now()));
    const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentsUploadedForAddMessage>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isMessageSent, setIsMessageSent] = useState(false);
    const [isQuickResponseLoadedFirstTime, setQuickResponseIsloadedFirstTime] = useState(true);
    const [isMessageInputEmpty, setIsMessageInputEmpty] = useState(true);
    const [selectedQuickResponseID, setSelectedQuickResponseID] = useState('');

    // Callback to track when drafts are saved to localStorage
    const handleDraftSaved = useCallback(() => {
      emitStoreMessageToLocalStorageEvent(user);
    }, [user]);

    const { clearMessageDrafts, getHasLocalStorageMessageDraft } = useMessageDrafts<
      AttachmentsUploadedForAddMessage[0]
    >({
      conversationId: conversationId || '',
      logger,
      messageInputText: inputText,
      setMessageInputText: setInputText,
      uploadedAttachments,
      setUploadedAttachments,
      onDraftSaved: handleDraftSaved,
    });

    useEffect(() => {
      /**
       * Revoke generated preview urls from AttachmentsUploader to
       * prevent memory leaks, will run on unmount
       * https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
       */
      return () =>
        uploadedAttachments.forEach((uploadedAttachment) => {
          if (uploadedAttachment.generatedPreviewUrl) {
            URL.revokeObjectURL(uploadedAttachment.generatedPreviewUrl);
          }
        });
    }, []);

    const { inquiry, isConversationBlockedByZillow, status } = conversationData?.ZRM_getConversationById || {};
    const { isSpam } = status || {};
    const { isPhoneLead } = inquiry || {};
    const disableComponent = Boolean(isPhoneLead || isImpersonator || isSpam || isConversationBlockedByZillow);
    const hasNoMessageText = formValidationUtils.isInputEmpty(inputText);
    const isAddMessageInputEmpty = hasNoMessageText && uploadedAttachments.length === 0;
    const isSendButtonDisabled = disableComponent || isAddMessageInputEmpty;
    const [shouldFocusAddMessage, setShouldFocusAddMessage] = useState(
      inboxPageIsAddMessageInputFocused && !inboxPageIsConversationSearchBarFocused && !disableComponent,
    );

    useEffect(() => {
      setIsMessageSent(false);
    }, [hasQuickResponses, fullMessageCountNumber]);

    useEffect(() => {
      inboxPageIsAddMessageInputFocusedVariable(true); // Focus input when attachments are added
    }, [uploadedAttachments]);

    /**
     * If inboxPageAddMessageInputTextToAppend has content, append it to current text
     * then reset inboxPageAddMessageInputTextToAppend to ''
     */
    useEffect(() => {
      if (inboxPageAddMessageInputTextToAppend) {
        const messageInput = inputText
          ? `${inputText} ${inboxPageAddMessageInputTextToAppend}`
          : inboxPageAddMessageInputTextToAppend;
        setInputText(messageInput);
        if (hasQuickResponses) {
          setSelectedQuickResponseID('');
          setIsTyping(true);
        }
        inboxPageAddMessageInputTextToAppendVariable('');
      }
    }, [inboxPageAddMessageInputTextToAppend, inputText, hasQuickResponses]);

    /**
     * If inboxPageClearAndAppendMessageInputText has content, clear the current text in the message input box
     * and append the content to current text. Then reset inboxPageClearAndAppendMessageInputText to ''
     */
    useEffect(() => {
      if (inboxPageClearAndAppendMessageInputText) {
        setInputText(inboxPageClearAndAppendMessageInputText);
        setIsMessageInputEmpty(false);
        inboxPageClearAndAppendMessageInputTextVariable('');
      }
    }, [inboxPageClearAndAppendMessageInputText]);

    const { setActiveModal, activeModal, clearModal } = useModal();
    const attachmentsUploaderIsOpened = ModalType.AttachmentsUploader === activeModal;
    const personalContactDetailsWarningModalIsOpened = ModalType.PersonalContactDetailsWarningModal === activeModal;

    /**
     * Allows us to persist attachment data until it is returned from the
     * next getAllForConversation poll in MessageList
     */
    let attachmentsForMessage: AttachmentsForMessage = [];
    const [addMessageMutation] = useAddMessage_AddMessageToConversationV2Mutation({
      update(cache, { data: addMessageResponse }) {
        setCurrentTempMessageId(String(Date.now()));

        const messageText = addMessageResponse?.ZRM_addMessageToConversationV2.message || '';
        const newMessage = {
          ...addMessageResponse?.ZRM_addMessageToConversationV2,
          attachments: attachmentsForMessage,
          messagePreview: getOptimisticMessagePreview({
            attachments: attachmentsForMessage,
            message: messageText,
          }),
        };

        const messageListData = cache.readQuery<
          MessageList_GetConversationQuery,
          MessageList_GetConversationQueryVariables
        >({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId!,
            listingAlias: listingAlias!,
            clientTimeZone,
          },
        });

        const currentMessages = messageListData?.ZRM_getConversationById.conversation || [];
        const failedMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
        const sentMessages = currentMessages.filter((message) => {
          const isFailed = message.messageId?.includes(MESSAGE_STATUS.FAILED);
          if (isFailed) {
            failedMessages.push(message);
          }
          return !isFailed;
        });

        const newConversation = [...sentMessages, newMessage, ...failedMessages];
        cache.writeQuery({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId || '',
            listingAlias: listingAlias || '',
            clientTimeZone,
          },
          data: {
            ZRM_getConversationById: {
              ...messageListData?.ZRM_getConversationById,
              conversation: newConversation,
            },
          },
        });

        if (!newMessage?.messageId?.includes('-')) {
          try {
            const conversationListData = cache.readQuery<
              ConversationList_GetConversationsQuery,
              ConversationList_GetConversationsQueryVariables
            >({
              query: ConversationList_GetConversationsDocument,
              variables: {
                listingAlias: isByProperty ? listingAlias : null,
                clientTimeZone,
                daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
                folder,
                readRepliedStatus,
                renterProgressStatus,
              },
            });

            let currentConversation: ConversationListConversation | undefined;
            const updatedConversations =
              conversationListData?.ZRM_getLatestConversationsV2?.conversations?.filter((conversation) => {
                if (conversation.conversationId === conversationId) {
                  currentConversation = conversation;
                }
                return conversation.conversationId != conversationId;
              }) || [];

            cache.writeQuery({
              query: ConversationList_GetConversationsDocument,
              variables: {
                listingAlias: isByProperty ? listingAlias : null,
                clientTimeZone,
                daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
                folder,
                readRepliedStatus,
                renterProgressStatus,
                // Custom flag used in ZRM_getLatestConversationsV2 apollo cache merge function
                skipMerge: true,
              },
              data: {
                ZRM_getLatestConversationsV2: {
                  ...conversationListData?.ZRM_getLatestConversationsV2,
                  conversations: currentConversation
                    ? [currentConversation, ...updatedConversations]
                    : updatedConversations,
                },
              },
            });
          } catch (error) {
            logError({
              error,
              context: {
                details: 'AddMessage - Failed to update conversation list cache after sending message',
                conversationId,
                fullStorySessionUrl: getCurrentSessionURL(),
                listingAlias,
              },
            });
          }

          // Update renter profile attachments when sending a message with attachments
          if (attachmentsForMessage.length) {
            try {
              const attachmentData = cache.readQuery<
                RenterProfile_GetAttachmentsByConversationIdQuery,
                RenterProfile_GetAttachmentsByConversationIdQueryVariables
              >({
                query: RenterProfile_GetAttachmentsByConversationIdDocument,
                variables: {
                  conversationId: conversationId!,
                },
              });

              const existingAttachments = attachmentData?.ZRM_getAttachmentsByConversationId
                ? attachmentData?.ZRM_getAttachmentsByConversationId.attachments
                : [];
              cache.writeQuery({
                query: RenterProfile_GetAttachmentsByConversationIdDocument,
                variables: {
                  conversationId: conversationId!,
                },
                data: {
                  ZRM_getAttachmentsByConversationId: {
                    ...attachmentData?.ZRM_getAttachmentsByConversationId,
                    attachments: [...attachmentsForMessage, ...existingAttachments],
                  },
                },
              });
            } catch (error) {
              logError({
                error,
                context: {
                  details: 'AddMessage - Failed to update renter profile attachments cache after sending message',
                  conversationId,
                  fullStorySessionUrl: getCurrentSessionURL(),
                  listingAlias,
                  attachmentsCount: attachmentsForMessage.length,
                },
              });
            }
          }
        }
      },
    });

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputText(e.target.value);
      if (hasQuickResponses) {
        setQuickResponseIsloadedFirstTime(false);
        setSelectedQuickResponseID('');
      }
      if (e.target.value === '') {
        if (hasQuickResponses) {
          setIsTyping(false);
        }
        setIsMessageInputEmpty(true);
      } else {
        if (hasQuickResponses) {
          setIsTyping(true);
        }
        setIsMessageInputEmpty(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' && !e.shiftKey && !isSendButtonDisabled && !isMobileDevice) {
        e.preventDefault();
        sendMessage();
      }
    };

    /**
     * Add Message to conversation flow
     * 1) Check for personal contact details and show warning modal if detected
     * 2) Add optimistic message to the conversation in the cache
     * 3) Kick off add message mutation to graphql server
     * 4a) If success, remove optimistic message from cache
     * 4b) If failure, change optimistic message type from 'sending' to 'failed' to change UI
     */
    const sendMessage = (bypassWarning = false) => {
      if (isAddMessageInputEmpty) {
        return;
      }

      // Check for personal contact details unless bypassing the warning
      if (!bypassWarning && enablePersonalInfoWarningModal && hasPersonalContactDetails(inputText)) {
        setActiveModal(ModalType.PersonalContactDetailsWarningModal);
        emitOpenPersonalInfoWarningModalEvent();
        return;
      }

      if (hasQuickResponses) {
        setIsMessageSent(true);
      }

      const messageText = inputText;
      const { attachmentsForOptimisticMessage = [], zDocIds = [] } = uploadedAttachments.length
        ? getAttachmentDataForAddMessageMutation(uploadedAttachments)
        : {};
      attachmentsForMessage = attachmentsForOptimisticMessage;

      const hasMessageDraft = getHasLocalStorageMessageDraft();
      if (hasMessageDraft) {
        emitSendDraftMessageEvent(user, { listingAlias });
      }

      clearMessageDrafts();

      // Optimistic UI
      const messagePreview = getOptimisticMessagePreview({
        attachments: attachmentsForMessage,
        message: messageText,
      });
      setCurrentTempMessageId(String(Date.now()));
      const optimisticMessageId = `temp-${currentTempMessageId}`;
      const normalizedMessageId = apolloClient.cache.identify({
        __typename: 'ZRM_Message',
        conversationId,
        messageId: optimisticMessageId,
      });
      const optimisticMessage = getOptimisticMessageObject({
        conversationId: conversationId || '',
        message: messageText,
        messagePreview,
        messageDateMs: currentTempMessageId,
        messageId: optimisticMessageId,
        attachments: attachmentsForOptimisticMessage,
      });

      const messageListData = apolloClient.cache.readQuery<
        MessageList_GetConversationQuery,
        MessageList_GetConversationQueryVariables
      >({
        query: MessageList_GetConversationDocument,
        variables: {
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
        },
      });

      const currentMessages = messageListData?.ZRM_getConversationById.conversation || [];
      const tempMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
      const sentMessages = currentMessages.filter((message) => {
        const isTemp = message.uiTreatmentType?.includes('-');

        if (isTemp) {
          tempMessages.push(message);
        }
        return !isTemp;
      });

      apolloClient.cache.writeQuery({
        query: MessageList_GetConversationDocument,
        variables: {
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
        },
        data: {
          ZRM_getConversationById: {
            ...messageListData?.ZRM_getConversationById,
            conversation: [...sentMessages, ...tempMessages, optimisticMessage],
          },
        },
      });

      const variables: MutationZrm_AddMessageToConversationV2Args = {
        conversationId: conversationId!,
        listingAlias: listingAlias,
        message: messageText,
        zDocIds,
      };

      if (hasQuickResponses && selectedQuickResponseID) {
        variables.quickResponseSuggestionId = selectedQuickResponseID;
      }

      addMessageMutation({
        variables: variables,
        onCompleted() {
          apolloClient.cache.evict({ id: normalizedMessageId });
          apolloClient.cache.gc();

          emitSendMessageEvent(user, { listingAlias });

          if (hasQuickResponses) {
            setSelectedQuickResponseID('');
            setIsTyping(false);
          }
        },
        onError(error) {
          // TODO RRMP-5361: after 2.0: Fix UI of telling user their message is too long
          logApolloError(error, {
            details: 'AddMessage - Add message failed. User seeing failed message treatment',
            conversationId,
            listingAlias,
            messageText,
          });

          // check error here
          apolloClient.cache.writeFragment({
            id: normalizedMessageId,
            fragment: gql`
              fragment OptimisticMessage on ZRM_Message {
                uiTreatmentType
              }
            `,
            data: {
              uiTreatmentType: 'lowPriority-failed',
            },
          });
        },
      });
    };

    const handleQuickResponeSelect = (selectedQuickResponseValue: string | null | undefined) => {
      setSelectedQuickResponseID(selectedQuickResponseValue || '');
    };

    // Keep the draft preserved by not clearing the input
    const handlePersonalContactWarningGoBack = () => {
      clearModal();
    };

    const handlePersonalContactWarningSendAnyway = () => {
      clearModal();
      emitSendAnywayInPersonalInfoWarningModalEvent();
      sendMessage(true);
    };

    const handleFocus = () => {
      inboxPageIsAddMessageInputFocusedVariable(true);
      setShouldFocusAddMessage(true);
    };

    const handleBlur = () => {
      inboxPageIsAddMessageInputFocusedVariable(false);
      setShouldFocusAddMessage(false);
    };

    const handleRemoveUploadedAttachment = useCallback(
      (zDocId: string) => {
        const filteredUploads = uploadedAttachments.filter((attachment) => attachment.zDocId !== zDocId);
        setUploadedAttachments(filteredUploads);
      },
      [uploadedAttachments],
    );

    const isAddAttachmentsDisabled = Boolean(isPhoneLead || isSpam || isConversationBlockedByZillow);
    const tooltipText = useMemo(() => {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      return '';
    }, [isSpam, isPhoneLead, isImpersonator, isConversationBlockedByZillow]);

    const containsUploadedAttachments = !!uploadedAttachments.length;
    const displayQuickResponseCTAs =
      enableQuickResponse &&
      hasQuickResponses &&
      !isTyping &&
      !attachmentsUploaderIsOpened &&
      !containsUploadedAttachments &&
      !isMessageSent;

    const placeholderText = isMobileDevice ? 'Write message...' : 'Type a message… (use Shift + Enter for a new line)';

    return (
      <>
        {attachmentsUploaderIsOpened && (
          <AttachmentsUploader
            existingUploadedAttachments={uploadedAttachments}
            setAttachmentsUploadedForAddMessage={setUploadedAttachments}
          />
        )}

        {personalContactDetailsWarningModalIsOpened && (
          <PersonalContactDetailsWarningModal
            warningData={personalContactWarningData}
            onGoBackClick={handlePersonalContactWarningGoBack}
            onSendAnywayClick={handlePersonalContactWarningSendAnyway}
          />
        )}

        <MessageListActions />

        <TooltipWrapper wrapInTooltip={disableComponent} tooltipText={tooltipText}>
          <AddMessageLayout
            ref={ref}
            isSendDisabled={isSendButtonDisabled}
            onSend={sendMessage}
            leftButtons={
              <AddAttachments
                isDisabled={isAddAttachmentsDisabled}
                setIsAttachmentsUploaderOpen={() => setActiveModal(ModalType.AttachmentsUploader)}
              />
            }
            sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
          >
            <TextareaAutoSize
              ariaLabel="Message input;"
              isDisabled={disableComponent}
              onBlur={handleBlur}
              onChange={handleTextAreaChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholderText}
              rows={1}
              shouldFocus={shouldFocusAddMessage}
              value={inputText}
            />

            {!!uploadedAttachments.length && (
              <UploadedAttachmentList
                uploadedAttachments={uploadedAttachments}
                handleRemoveUploadedAttachment={handleRemoveUploadedAttachment}
              />
            )}
          </AddMessageLayout>
        </TooltipWrapper>

        {displayQuickResponseCTAs && (
          <QuickResponse
            quickResponses={quickResponses}
            isQuickResponseLoadedFirstTime={isQuickResponseLoadedFirstTime}
            isMessageInputEmpty={isMessageInputEmpty}
            onQuickResponseSelect={handleQuickResponeSelect}
          />
        )}
      </>
    );
  },
);

AddMessage.displayName = 'AddMessage';
export default AddMessage;

const GET_ADD_MESSAGE_INPUT_TEXT = gql`
  query {
    inboxPageAddMessageInputTextToAppend @client
    inboxPageIsAddMessageInputFocused @client
    inboxPageIsConversationSearchBarFocused @client
    inboxPageClearAndAppendMessageInputText @client
  }
`;

const getOptimisticMessageObject = ({
  conversationId,
  messageDateMs,
  message,
  messagePreview,
  messageId,
  attachments,
}: {
  conversationId: string;
  messageDateMs: string;
  message: string;
  messagePreview: string;
  messageId: string;
  attachments: AttachmentsForMessage;
}) => ({
  __typename: 'ZRM_Message',
  aiAutoResponseType: null,
  conversationId,
  messageDateMs,
  messageType: 'listingMessage',
  message,
  messagePreview,
  senderName: '',
  uiTreatmentType: 'lowPriority-sending',
  attachments,
  isMessageOwner: true,
  messageLink: {
    url: '',
    text: '',
    __typename: 'ZRM_CTA_Link',
  },
  messageId,
  title: '',
});

const getOptimisticMessagePreview = ({
  attachments,
  message,
}: {
  attachments: AttachmentsForMessage;
  message: string;
}) => {
  const shouldShowAttachmentTextPreview = attachments.length && !message;
  const attachmentTextPreview = `${attachments.length} ${stringUtils.pluralize('attachment', attachments.length)}`;
  return shouldShowAttachmentTextPreview ? attachmentTextPreview : message;
};

const getAttachmentDataForAddMessageMutation = (uploadedAttachments: AttachmentsUploadedForAddMessage) => {
  const zDocIds: Array<string> = [];
  const attachmentsForOptimisticMessage = uploadedAttachments.map((uploadedAttachment) => {
    const { file, generatedPreviewUrl, zDocId } = uploadedAttachment;
    const attachmentAdded = new Date().getTime();

    if (zDocId) {
      zDocIds.push(zDocId);
    }

    return {
      __typename: 'ZRM_Attachment',
      attachmentAddedMs: attachmentAdded.toString(),
      attachmentMetadataStatus: 'complete',
      downloadUrl: '',
      fileName: file.name,
      fileSizeBytes: file.size,
      fileType: file.type,
      isAttachmentDeleted: false,
      isAttachmentExpired: false,
      previewUrl: generatedPreviewUrl,
      zDocId,
    } as Zrm_Attachment;
  });

  return {
    attachmentsForOptimisticMessage,
    zDocIds,
  };
};
